import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Graph from '../components/Graph';
import { transactionService, knownAddressService } from '../services/api';
import '../styles/TracePage.css';

function fmtAddr(addr) {
  if (!addr) return '—';
  return `${addr.slice(0, 10)}...${addr.slice(-6)}`;
}

function fmtDate(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
    }) + ' UTC';
  } catch { return String(ts); }
}

function fmtValue(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(2)}K`;
  return n.toFixed(4);
}

// Degree-based risk scoring — mirrors graph.py's simple method.
// Returns 0–10. Called after degrees are tallied in buildGraph.
function calcRiskScore(node) {
  const inD  = node.in_degree  || 0;
  const outD = node.out_degree || 0;
  const total = inD + outD;
  if (total === 0) return 0;
  // High fan-out ratio → higher risk
  const fanOutRatio = outD / total;
  let score = fanOutRatio * 5;
  // Many transactions add to risk
  if (node.tx_count >= 50) score += 3;
  else if (node.tx_count >= 20) score += 2;
  else if (node.tx_count >= 10) score += 1;
  // Known mixers/sanctioned addresses get max risk — applied after enrichment
  return Math.min(10, score);
}

export default function TracePage() {
  const { blockchain, address } = useParams();
  const [searchParams]          = useSearchParams();

  // Context passed from the parent window via URL params
  const fromAddress  = searchParams.get('from')        || '';
  const txHash       = searchParams.get('tx')          || '';
  const hopDepth     = parseInt(searchParams.get('hop') || '1', 10);
  const windowStart  = searchParams.get('windowStart') || '';
  const windowEnd    = searchParams.get('windowEnd')   || '';
  const txValue      = searchParams.get('value')       || '';
  const txToken      = searchParams.get('token')       || '';
  const invId        = searchParams.get('invId')       || '';

  const [transactions, setTransactions] = useState([]);
  const [graphData,    setGraphData]    = useState(null);
  const [knownInfo,    setKnownInfo]    = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [hiddenEdges,  setHiddenEdges]  = useState(new Set());
  const [hiddenNodes,  setHiddenNodes]  = useState(new Set());
  const [selectedNode, setSelectedNode] = useState(null);

  // Make trace tabs/windows distinguishable (fixes "two Cryptcrawler tabs" confusion)
  useEffect(() => {
    if (!address) return;
    const short = `${address.slice(0, 6)}…${address.slice(-4)}`;
    const chain = (blockchain || '').toUpperCase();
    document.title = `Hop ${hopDepth} • ${chain} • ${short}`;
  }, [blockchain, address, hopDepth]);

  // Trace-page graph hiding is local to the current hop window only.
  useEffect(() => {
    setHiddenEdges(new Set());
    setHiddenNodes(new Set());
  }, [blockchain, address, hopDepth]);

  // Build raw graph from a flat tx list, filtered to crime window.
  // Does NOT do async work — enrichment happens in the load effect.
  const buildGraph = useCallback((txs) => {
    const wsDate = windowStart ? new Date(windowStart) : null;
    const weDate = windowEnd   ? new Date(windowEnd)   : null;

    const filtered = txs.filter(tx => {
      if (!wsDate && !weDate) return true;
      const ts = tx.timestamp ? new Date(tx.timestamp) : null;
      if (!ts) return true;
      if (wsDate && ts < wsDate) return false;
      if (weDate && ts > weDate) return false;
      return true;
    });

    const nodeMap  = {};
    const edges    = [];
    const seenEdge = new Set();

    filtered.forEach((tx, i) => {
      const src = tx.from_address || '';
      const tgt = tx.to_address   || '';
      if (!src || !tgt) return;

      [src, tgt].forEach(addr => {
        if (!nodeMap[addr]) {
          nodeMap[addr] = {
            id:         addr,
            address:    addr,
            blockchain: blockchain || 'tron',
            tx_count:   0,
            in_degree:  0,
            out_degree: 0,
            risk_score: 0,
          };
        }
      });
      nodeMap[src].out_degree += 1;
      nodeMap[src].tx_count   += 1;
      nodeMap[tgt].in_degree  += 1;
      nodeMap[tgt].tx_count   += 1;

      const edgeKey = `${src}-${tgt}-${tx.tx_hash || i}`;
      if (!seenEdge.has(edgeKey)) {
        seenEdge.add(edgeKey);
        edges.push({
          id:           `${src}-${tgt}-${i}`,
          from:         src,
          to:           tgt,
          source:       src,
          target:       tgt,
          value:        tx.value        || 0,
          tx_hash:      tx.tx_hash      || '',
          timestamp:    tx.timestamp    || '',
          token_symbol: tx.token_symbol || '',
          status:       tx.status       || 'success',
        });
      }
    });

    // Apply degree-based risk scores now that degrees are fully tallied
    const nodes = Object.values(nodeMap).map(n => ({
      ...n,
      risk_score: calcRiskScore(n),
    }));

    return { nodes, edges };
  }, [windowStart, windowEnd, blockchain]);

  const openTraceWindow = useCallback((recipient, extra = {}) => {
    if (!recipient || recipient === address) return;

    const params = new URLSearchParams({
      from: address,
      hop:  String(hopDepth + 1),
      ...(windowStart && { windowStart }),
      ...(windowEnd   && { windowEnd   }),
      ...(invId       && { invId       }),
      ...extra,
    });

    // Try to offset pop-up windows so they don't stack perfectly on top of each other.
    // Browsers may still choose to open in a tab; focus() helps either way.
    const left = (window.screenX ?? window.screenLeft ?? 0) + 40;
    const top  = (window.screenY ?? window.screenTop  ?? 0) + 40;
    const features = `noopener,noreferrer,width=1200,height=820,left=${left},top=${top}`;

    const w = window.open(
      `/trace/${blockchain}/${recipient}?${params.toString()}`,
      '_blank',
      features,
    );
    try { w?.focus(); } catch {}
  }, [address, blockchain, hopDepth, invId, windowEnd, windowStart]);

  // Load transactions, then bulk-lookup known addresses and stamp risk.
  // Known mixer/sanctioned nodes get risk bumped to 9.5.
  useEffect(() => {
    if (!blockchain || !address) return;
    setLoading(true);
    setError(null);

    Promise.all([
      transactionService.getForAddress(blockchain, address),
      knownAddressService.lookup(address).catch(() => ({ data: null })),
    ])
      .then(([txRes, knownRes]) => {
        const txs  = Array.isArray(txRes.data) ? txRes.data : [];
        setTransactions(txs);
        setKnownInfo(knownRes.data || null);

        const raw = buildGraph(txs);

        // Bulk-lookup all node addresses for known-address enrichment
        const addrs = raw.nodes.map(n => n.id).filter(Boolean);
        return knownAddressService.bulkLookup(addrs)
          .then(bulkRes => {
            const knownMap = bulkRes.data || {};
            const HIGH_RISK_TYPES = new Set(['mixer', 'sanctioned']);
            const enrichedNodes = raw.nodes.map(n => {
              const known = knownMap[n.id];
              const riskBoost = known && HIGH_RISK_TYPES.has(known.address_type) ? 9.5 : 0;
              return {
                ...n,
                risk_score: riskBoost > 0 ? riskBoost : n.risk_score,
              };
            });
            setGraphData({ nodes: enrichedNodes, edges: raw.edges });
          })
          .catch(() => {
            // Bulk-lookup failed — use un-enriched graph (still has degree risk)
            setGraphData(raw);
          });
      })
      .catch(err => {
        setError('Failed to load transactions: ' + (err.response?.data?.detail || err.message));
      })
      .finally(() => setLoading(false));
  }, [blockchain, address, buildGraph]);

  // Follow a transaction — open a new window one hop deeper
  const handleFollowTx = useCallback((tx) => {
    const recipient = tx.to || tx.to_address || tx.target;
    openTraceWindow(recipient, {
      tx:    tx.tx_hash || tx.id || '',
      value: String(tx.value || ''),
      token: tx.token_symbol || '',
    });
  }, [openTraceWindow]);

  // Follow by selecting a node (no specific tx context, but advances hop)
  const handleFollowAddress = useCallback((recipient) => {
    openTraceWindow(recipient);
  }, [openTraceWindow]);

  const handleViewStateChange = useCallback((newHiddenEdges, newHiddenNodes) => {
    setHiddenEdges(new Set(newHiddenEdges));
    setHiddenNodes(new Set(newHiddenNodes));
  }, []);

  // Outbound txs only (from this address), sorted by date desc
  const outbound = transactions
    .filter(tx => (tx.from_address || '').toLowerCase() === address.toLowerCase())
    .sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp) : 0;
      const tb = b.timestamp ? new Date(b.timestamp) : 0;
      return tb - ta;
    });

  // Window-filtered outbound (what we show on graph + table)
  const wsDate = windowStart ? new Date(windowStart) : null;
  const weDate = windowEnd   ? new Date(windowEnd)   : null;
  const filteredOutbound = outbound.filter(tx => {
    if (!wsDate && !weDate) return true;
    const ts = tx.timestamp ? new Date(tx.timestamp) : null;
    if (!ts) return true;
    if (wsDate && ts < wsDate) return false;
    if (weDate && ts > weDate) return false;
    return true;
  });

  const hopColors = { 0: '#00d9ff', 1: '#f59e0b', 2: '#a855f7', 3: '#ef4444', 4: '#10b981' };
  const myColor   = hopColors[hopDepth] ?? '#6b7280';

  return (
    <div className="trace-page">

      {/* ── Header bar ── */}
      <div className="trace-header">
        <div className="trace-header-left">
          <div className="trace-hop-badge" style={{ background: myColor }}>
            Hop {hopDepth}
          </div>
          <div className="trace-chain-badge">{blockchain?.toUpperCase()}</div>
          {knownInfo && (
            <div className="trace-known-badge">
              🏷️ {knownInfo.name} ({knownInfo.address_type})
            </div>
          )}
        </div>
        <div className="trace-header-right">
          {windowStart && windowEnd && (
            <div className="trace-window-pill">
              📅 {fmtDate(windowStart)} → {fmtDate(windowEnd)}
            </div>
          )}
          <button className="trace-close-btn" onClick={() => window.close()}>✕ Close</button>
        </div>
      </div>

      {/* ── Wallet context bar ── */}
      <div className="trace-wallet-bar">
        {fromAddress && (
          <div className="trace-breadcrumb">
            <span className="trace-breadcrumb-from" title={fromAddress}>
              {fmtAddr(fromAddress)}
            </span>
            <span className="trace-breadcrumb-arrow">→</span>
            {txHash && (
              <>
                <span className="trace-breadcrumb-tx" title={txHash}>
                  TX {txHash.slice(0, 10)}…
                </span>
                {(txValue || txToken) && (
                  <span className="trace-breadcrumb-value">
                    {txValue && fmtValue(txValue)} {txToken}
                  </span>
                )}
                <span className="trace-breadcrumb-arrow">→</span>
              </>
            )}
          </div>
        )}
        <div className="trace-address-main">
          <span className="trace-address-label">Wallet</span>
          <code className="trace-address-code">{address}</code>
          <button
            className="trace-copy-btn"
            onClick={() => navigator.clipboard.writeText(address)}
            title="Copy address"
          >📋</button>
        </div>
      </div>

      {/* ── Stats strip ── */}
      {!loading && !error && (
        <div className="trace-stats-strip">
          <div className="trace-stat">
            <span>Total TXs</span>
            <strong>{transactions.length}</strong>
          </div>
          <div className="trace-stat">
            <span>Outbound in window</span>
            <strong>{filteredOutbound.length}</strong>
          </div>
          <div className="trace-stat">
            <span>Unique recipients</span>
            <strong>{new Set(filteredOutbound.map(t => t.to_address)).size}</strong>
          </div>
          {filteredOutbound.length > 0 && (
            <div className="trace-stat">
              <span>Total sent</span>
              <strong>
                {fmtValue(filteredOutbound.reduce((s, t) => s + (t.value || 0), 0))}{' '}
                {filteredOutbound[0]?.token_symbol || ''}
              </strong>
            </div>
          )}
        </div>
      )}

      {/* ── Main content ── */}
      {loading && (
        <div className="trace-loading">
          <div className="trace-spinner" />
          Loading transactions for {fmtAddr(address)}…
        </div>
      )}

      {error && <div className="trace-error">{error}</div>}

      {!loading && !error && (
        <div className="trace-body">

          {/* Graph */}
          <div className="trace-graph-section">
            <div className="trace-section-title">
              Transaction Graph
              {wsDate && weDate && (
                <span className="trace-section-sub">filtered to crime window</span>
              )}
            </div>
            {graphData?.nodes?.length > 0 ? (
              <div className="trace-graph-wrap" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Graph
                    data={graphData}
                    startAddress={address}
                    hiddenEdges={hiddenEdges}
                    hiddenNodes={hiddenNodes}
                    onViewStateChange={handleViewStateChange}
                    onNodeSelect={setSelectedNode}
                  />
                </div>

                {/* Selected-node side panel — mirrors Graph's selection, same pattern as
                    InvestigationDetail's sidebar. No in-canvas floating panel here either. */}
                {selectedNode && (
                  <div style={{
                    width: 260, flexShrink: 0,
                    background: 'rgba(10,15,30,0.85)',
                    border: '1px solid rgba(0,217,255,0.2)',
                    borderRadius: 8, padding: 14,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#00d9ff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {selectedNode.id === address ? '★ Start Address' : 'Selected Address'}
                      </span>
                      <button onClick={() => setSelectedNode(null)}
                        style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer' }}>✕</button>
                    </div>
                    <code className="trace-address-code" style={{ display: 'block', marginBottom: 10, wordBreak: 'break-all' }}>
                      {selectedNode.id}
                    </code>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                      <div className="trace-stat" style={{ border: 'none', padding: 0 }}>
                        <span>Risk Score</span>
                        <strong>{(selectedNode._raw?.risk_score || 0).toFixed(1)}/10</strong>
                      </div>
                      <div className="trace-stat" style={{ border: 'none', padding: 0 }}>
                        <span>Transactions</span>
                        <strong>{selectedNode._raw?.tx_count || 0}</strong>
                      </div>
                    </div>
                    {selectedNode.id !== address && (
                      <button className="trace-follow-btn" style={{ width: '100%' }}
                        onClick={() => handleFollowAddress(selectedNode.id)}>
                        🔍 Trace this wallet →
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="trace-empty">No transactions found in this window for this wallet.</div>
            )}
          </div>

          {/* Outbound TX table */}
          <div className="trace-table-section">
            <div className="trace-section-title">
              Outbound Transactions
              {wsDate && weDate
                ? <span className="trace-section-sub">{filteredOutbound.length} within crime window</span>
                : <span className="trace-section-sub">{outbound.length} total</span>
              }
            </div>

            {filteredOutbound.length === 0 ? (
              <div className="trace-empty">
                No outbound transactions{wsDate ? ' within the crime window' : ''}.
                {wsDate && outbound.length > 0 && (
                  <span> ({outbound.length} outbound exist outside the window.)</span>
                )}
              </div>
            ) : (
              <div className="trace-table-scroll">
                <table className="trace-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date (UTC)</th>
                      <th>To (Recipient)</th>
                      <th>Amount</th>
                      <th>Token</th>
                      <th>TX Hash</th>
                      <th>Follow</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOutbound.map((tx, i) => (
                      <tr key={i}>
                        <td className="trace-td-num">{i + 1}</td>
                        <td className="trace-td-date">{fmtDate(tx.timestamp)}</td>
                        <td>
                          <code className="trace-addr" title={tx.to_address}>
                            {fmtAddr(tx.to_address)}
                          </code>
                        </td>
                        <td className="trace-td-value">{fmtValue(tx.value)}</td>
                        <td>{tx.token_symbol || '—'}</td>
                        <td>
                          <code className="trace-hash" title={tx.tx_hash}>
                            {tx.tx_hash ? tx.tx_hash.slice(0, 12) + '…' : '—'}
                          </code>
                        </td>
                        <td>
                          <button
                            className="trace-follow-btn"
                            onClick={() => handleFollowTx({
                              to:           tx.to_address,
                              tx_hash:      tx.tx_hash,
                              value:        tx.value,
                              token_symbol: tx.token_symbol,
                            })}
                            title={`Follow funds to ${fmtAddr(tx.to_address)}`}
                          >
                            Follow →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}